import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { apiGet } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Card, SectionHeader } from '@/components/ui/Card';
import { Money } from '@/components/ui/Money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pill } from '@/components/ui/Pill';
import { ErrorState, LoadingState } from '@/components/ui/States';
import type { ProductRow } from '@/types/api';
import { num } from '@/utils/format';

interface ProductFull extends ProductRow {
  variants?: {
    id: number;
    color?: string;
    size?: string;
    model?: string;
    barcode?: string;
    is_default?: boolean;
    stock_levels?: { branch_id: number; quantity: number; branch?: { name: string } }[];
  }[];
  images?: { id: number; image_url: string }[];
}

/**
 * Read-only product detail. The full edit flow is on mobile (the
 * 4-step wizard). On web we surface key facts + per-branch stock so
 * a manager can see "do we have 3 of these in our Bandra branch?"
 * without opening the phone. Bulk price edits live in the bulk-import
 * page rather than here, deliberately — single-product mutations are
 * better done where the user is looking at the cashier flow.
 */
export function ProductDetailPage() {
  const { id } = useParams();
  const productId = Number(id);
  const query = useQuery({
    queryKey: ['product', productId],
    queryFn: () => apiGet<ProductFull>(endpoints.product(productId)),
    enabled: Number.isFinite(productId),
  });

  if (!Number.isFinite(productId)) return <ErrorState title="Invalid product id" />;
  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        title="Couldn't load product"
        description={(query.error as Error)?.message}
        onRetry={() => query.refetch()}
      />
    );
  }

  const p = query.data;
  const variants = p.variants ?? [];

  return (
    <>
      <PageHeader
        back
        title={p.name}
        description={
          [p.brand?.name, p.category?.name].filter(Boolean).join(' · ') ||
          'Uncategorised'
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card>
          <SectionHeader title="Pricing" />
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-ink-soft">Selling price</dt>
            <dd className="text-right">
              <Money value={p.selling_price} className="font-semibold" />
            </dd>
            <dt className="text-ink-soft">MRP</dt>
            <dd className="text-right">
              {p.mrp ? <Money value={p.mrp} /> : '—'}
            </dd>
            <dt className="text-ink-soft">Purchase price</dt>
            <dd className="text-right">
              {p.purchase_price ? <Money value={p.purchase_price} /> : '—'}
            </dd>
            <dt className="text-ink-soft">GST rate</dt>
            <dd className="text-right">{p.gst_rate ? `${p.gst_rate}%` : '—'}</dd>
            <dt className="text-ink-soft">HSN</dt>
            <dd className="text-right font-mono text-xs">{p.hsn_code || '—'}</dd>
            <dt className="text-ink-soft">Unit</dt>
            <dd className="text-right">{p.unit || 'piece'}</dd>
          </dl>
        </Card>

        <Card>
          <SectionHeader title="Tracking" />
          <div className="space-y-3 text-sm">
            <div>
              <div className="text-ink-soft text-xs uppercase mb-1">Mode</div>
              <Pill tone="brand">
                {p.unit_tracking_mode?.replace(/_/g, ' ') ?? 'quantity only'}
              </Pill>
            </div>
            <div>
              <div className="text-ink-soft text-xs uppercase mb-1">SKU</div>
              <div className="font-mono">{p.sku || '—'}</div>
            </div>
            <div>
              <div className="text-ink-soft text-xs uppercase mb-1">Variants</div>
              <div>{variants.length}</div>
            </div>
          </div>
        </Card>

        <Card>
          <SectionHeader title="Total stock" />
          <div className="text-3xl font-bold tabular-nums">
            {num(p.stock ?? 0)}
          </div>
          <div className="text-sm text-ink-soft mt-1">
            Across all branches and variants
          </div>
        </Card>
      </div>

      {/* Variants × branches matrix */}
      <Card padded={false}>
        <div className="px-5 py-4 border-b border-line">
          <h2 className="text-base font-semibold">Per-branch stock</h2>
        </div>
        {variants.length === 0 ? (
          <div className="px-5 py-12 text-center text-ink-soft">
            No variants yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr className="text-left text-xs uppercase tracking-wide text-ink-soft">
                  <th className="px-5 py-2">Variant</th>
                  <th className="px-5 py-2">Barcode</th>
                  <th className="px-5 py-2 text-right">Stock</th>
                </tr>
              </thead>
              <tbody>
                {variants.map((v) => {
                  const total =
                    v.stock_levels?.reduce(
                      (s, lvl) => s + Number(lvl.quantity ?? 0),
                      0,
                    ) ?? 0;
                  const label =
                    [v.color, v.size, v.model].filter(Boolean).join(' / ') ||
                    (v.is_default ? 'Default' : 'Variant');
                  return (
                    <tr key={v.id} className="border-t border-line">
                      <td className="px-5 py-3">
                        <div className="font-medium">{label}</div>
                        {(v.stock_levels ?? []).length > 0 && (
                          <div className="text-xs text-ink-soft mt-1">
                            {v.stock_levels!
                              .map(
                                (l) =>
                                  `${l.branch?.name ?? `Branch ${l.branch_id}`}: ${num(l.quantity)}`,
                              )
                              .join(' · ')}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs">
                        {v.barcode || '—'}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums font-semibold">
                        {num(total)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
